import {yupResolver} from "@hookform/resolvers/yup";
import {useEffect} from "react";
import {FormProvider, useForm} from "react-hook-form";
import * as Yup from "yup";

import {FileInputField} from "@/components/form/fields/FileInputField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {ServerErrors} from "@/lib/types/common.ts";

export interface ImportSubscribersFormValues {
  file: FileList;
}

const ImportSubscribersFormSchema = Yup.object().shape({
  file: Yup.mixed<FileList>()
    .required("File is required")
    .test("fileList", "Please select a file", (value) => {
      return value instanceof FileList && value.length > 0;
    })
    .test("fileType", "Only Excel (.xlsx) or CSV files are allowed", (value) => {
      if (!(value instanceof FileList) || value.length === 0) return false;
      const file = value[0];
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ];
      return validTypes.includes(file.type);
    }),
});

interface ImportSubscribersFormProps {
  formID: string;
  show: boolean;
  serverErrors?: ServerErrors;
  onSubmit: (values: ImportSubscribersFormValues) => void;
}

export const ImportSubscribersForm = ({formID, show, serverErrors, onSubmit}: ImportSubscribersFormProps) => {
  const methods = useForm<ImportSubscribersFormValues>({
    resolver: yupResolver<ImportSubscribersFormValues>(ImportSubscribersFormSchema),
  });
  const {handleSubmit, reset} = methods;
  useEffect(() => {
    if (!show) {
      reset();
    }
  }, [show, reset]);
  return (
    <FormProvider {...methods}>
      <form id={formID} onSubmit={handleSubmit(onSubmit)}>
        <div className="pulseTextBody mb-3">
          Using this form you may upload subscribers in bulk into the system. Please download our{" "}
          <a download href={`${URL_ORIGINAL_WEBSITE}/static/statuspages/downloads/subscriber_import_template.xltx`}>
            Bulk Upload Excel Template
          </a>
          , fill it out and submit it using the form below. <br />
          <br />
          Limited to importing 1000 subscribers at a time.
        </div>
        {serverErrors?.__all__ && (
          <div className="mb-3">
            <FormErrorAlerts errors={serverErrors.__all__} />
          </div>
        )}
        <FileInputField label={"Upload XLSX/CSV"} name="file" required={true} placeholder={"Select a file"} />
        <a
          download
          className="pulseTextBodySmall"
          href={`${URL_ORIGINAL_WEBSITE}/static/statuspages/downloads/subscriber_import_sample.xlsx`}
        >
          View sample file
        </a>
      </form>
    </FormProvider>
  );
};
